import React, { FC } from 'react';
import {
    Box,
    Button,
    ButtonProps,
    Divider,
    Flex,
    Heading,
    Link,
    LinkProps,
    Text
} from '@chakra-ui/react';
import CircleIcon from 'ts/common/components/gallery/CircleIcon';
import { BundleFileIcon, DownloadIcon, SlideshowIcon } from 'ts/common/components/icons';

const HelpfulTipsButton: FC<ButtonProps & LinkProps> = ({ children, ...rest }) => (
    <Button
        as={Link}
        target="_blank"
        rel="noopener noreferrer"
        borderColor="gray.400"
        color="text"
        fontWeight="semibold"
        variant="outline"
        width={{ base: 'full', lg: '358px' }}
        _active={{
            backgroundColor: 'initial'
        }}
        _focus={{
            boxShadow: 'none'
        }}
        _focusVisible={{
            boxShadow: 'outline'
        }}
        _hover={{
            backgroundColor: 'initial',
            textDecoration: 'none'
        }}
        _visited={{
            color: 'text'
        }}
        {...rest}
    >
        {children}
    </Button>
);

export interface IBundleFile {
    filename: string;
    size: string;
}

export interface IBundleVideo {
    filename: string;
    url: string;
}

export type IBundleSlideshow = IBundleVideo;

export const getFileLabel = (file: IBundleFile) => `${file.filename} (${file.size})`;

interface Props {
    /** A list of files to be rendered */
    files: IBundleFile[];
    /** A list of videos to be rendered. Videos are not zipped; they are direct download links. */
    videos?: IBundleVideo[];
    /** A list of slideshows to be rendered. Slideshows are not zipped either; their exported
     *  video is a direct download link, same as a video. */
    slideshows?: IBundleSlideshow[];
    /** A function to be called when a file, video, or slideshow is clicked */
    onClickFile: (file: IBundleFile | IBundleVideo | IBundleSlideshow) => unknown;
    /** The Angular translation service */
    translateFilter: SpAngularJs.ITranslateFilter;
}

const BundleFileList: FC<Props> = ({
    files,
    videos = [],
    slideshows = [],
    onClickFile,
    translateFilter: translate
}) => {
    // A 1-D list (icon on the left, name/size stacked to its right) rather
    // than a grid of icon-above-name columns - a long name now has the
    // item's full width to wrap into instead of being squeezed into (or
    // overflowing past) a narrow fixed-width column, and doesn't need its
    // own centering since the whole row is left-aligned.
    const renderItem = (
        item: IBundleFile | IBundleVideo | IBundleSlideshow,
        key: string,
        ariaLabel: string,
        icon: typeof BundleFileIcon,
        size?: string
    ) => (
        <Button
            key={key}
            onClick={() => onClickFile(item)}
            aria-label={ariaLabel}
            alignItems="center"
            borderRadius="0px"
            borderStyle="none"
            color="brand.primary"
            display="flex"
            gap="16px"
            height="auto"
            justifyContent="flex-start"
            textDecoration="underline"
            variant="link"
            whiteSpace="normal"
            width="full"
            _focus={{
                boxShadow: 'none'
            }}
            _focusVisible={{
                boxShadow: 'outline'
            }}
        >
            <CircleIcon icon={icon} size="lg" />
            <Flex direction="column" textAlign="left">
                <Text
                    color="brand.primary"
                    fontSize="md"
                    fontWeight="semibold"
                    letterSpacing="0px"
                    overflowWrap="break-word"
                    title={item.filename}
                >
                    {item.filename}
                </Text>
                {size && <Text color="brand.primary">{size}</Text>}
            </Flex>
        </Button>
    );

    return (
        <Flex alignItems="center" flexDirection="column">
            <Flex direction="column" gap="24px" width={{ base: 'full', lg: '358px' }}>
                {files.map((file) =>
                    renderItem(file, file.filename, getFileLabel(file), BundleFileIcon, file.size)
                )}
                {videos.map((video) => renderItem(video, video.url, video.filename, DownloadIcon))}
                {slideshows.map((slideshow) =>
                    renderItem(slideshow, slideshow.url, slideshow.filename, SlideshowIcon)
                )}
            </Flex>
            <Divider marginY="32px" />
            <Box marginBottom="24px">
                <Heading as="h3" marginY="16px" size="md" variant="primary">
                    {translate('zipBundleHelpfulTips')}
                </Heading>
                {/* Only files are actually delivered as a .zip - videos and
                    slideshows are direct download links (see the props doc
                    above) - so this tip is meaningless when there's no zip
                    file on the page. */}
                {files.length > 0 && (
                    <Text fontSize="md" marginY="24px">
                        {translate('zipBundleHelpfulTipsItem1')}
                    </Text>
                )}
                <Text fontSize="md" marginY="24px">
                    {translate('zipBundleHelpfulTipsItem2')}
                </Text>
            </Box>
            <Flex alignItems="center" alignSelf="stretch" flexDirection="column" gap="16px">
                <HelpfulTipsButton href="https://support.apple.com/kb/PH25411">
                    macOS
                </HelpfulTipsButton>
                <HelpfulTipsButton href="https://support.microsoft.com/help/14200/windows-compress-uncompress-zip-files">
                    Windows
                </HelpfulTipsButton>
            </Flex>
        </Flex>
    );
};

export default BundleFileList;
